/**
 * The ch00 feature manifest — the substrate's own screens.
 *
 * Five routes, one per F-class that has a planner-facing surface:
 *
 * | Route | reqs/00 | What it is for |
 * |---|---|---|
 * | `hierarchies` | F2, D4 | Coexisting versioned schemes, and the reversibility proof |
 * | `plans` | F3, D5 | Version browser and cell-level diff |
 * | `scenarios` | F4 | Side-by-side compare with the financial bridge |
 * | `exceptions` | F5 | The queue — G1's only inbox: by group (DEC-101, ADR 9040) or one alert per row |
 * | `exceptions/group` | F5, DEC-101 | One alert group: its alerts, sub-groups and the acts on the set |
 * | `feeds` | D10, bata-ui#166 W1-1/W1-2 | Data, feeds: every feed's batch in force, freshness against its contract and double counting, per market |
 * | `schedules` | bata-ui#166 W1-1/W1-2 | Data, schedules: every schedule the orchestrator reports for a market — its own page since package A (client rule 3), a sub-entry of Data |
 * | `modules` | bata-ui#166 W1-7, DEC-06 | Per market, whether each part of the batch runs, its cadence and excluded doors — changed only by a registry proposal |
 * | `parameters` | G1 | The effective-dated parameter register, its pending-approvals queue, and the reset AND the authored propose that both write through it |
 * | `objective-configs` | `17` §1 P2 | Propose a P2 objective-configuration term — `docs/adr/2001` |
 * | `autonomy` | reqs/07 §3.5 | The decision-class autonomy register, read-only — `docs/adr/2003` |
 *
 * Every data route declares `exports` and `drill`, because the manifest types make both
 * required on a data route — G2 tests *d* and *f* are type errors here rather than
 * census failures later.
 *
 * `documentation` is the one non-data route: a planner-facing explanation of the
 * chapters and the concepts that recur across them, so `exports`/`drill` are `never`
 * on it rather than omitted by convention.
 */

import { defineFeature } from '../manifest';
import { FOUNDATIONS_GUIDES } from './guides';
import { SCHEDULES_EXPORT_ID, schedulesExport } from './schedules-exports';
import { ACTIVITY_EXPORT_ID, activityExport } from './activity';
import {
  USER_ACTIONS_EXPORT_ID, USER_ACTIVITY_EXPORT_ID, USER_SESSIONS_EXPORT_ID, userActionsExport, userActivityExport, userSessionsExport,
} from './user-activity';

import {
  alertGroupExport,
  autonomyRegisterExport,
  exceptionQueueExport,
  exceptionRulesExport,
  feedStatusExport,
  hierarchyExport,
  modulesExport,
  parameterApprovalsExport,
  parameterHistoryExport,
  parameterRegistryExport,
  planVersionExport,
  processCalendarExport,
  scenarioListExport,
} from './exports';

export default defineFeature({
  slug: 'foundations',
  titleKey: 'foundations.title',
  navLabelKey: 'foundations.navLabel',
  icon: 'database',  // brand guide p.14
  railGroup: 'setup',  // bata-ui#132 — configuration and analysis
  localeNamespace: 'foundations',
  // Every route's guide and guided tour (ADR 9044) — one entry per route below, or the census fails.
  guides: FOUNDATIONS_GUIDES,
  navigation: [
    // Parameters first (bata-ui#132 Step 3): the planning parameters are what a planner most
    // often comes to this chapter for, and sixth of eight they were not found. The orders
    // stay inside this chapter's own 10–17 band, so the flat cross-chapter order is unchanged.
    // DEC-89 (2026-09-30): the register opens on its table, and what stood above it — the
    // expiries and the change record — is its sub-page «Scadenze e storico». Management's rail
    // draws both as sub-entries of «Parametri generali»; every app's «Parametri» opens the first.
    {
      labelKey: 'foundations.nav.parameters',
      to: '/foundations/parameters',
      order: 10,
      children: [
        { labelKey: 'foundations.nav.parameterRegister', to: '/foundations/parameters', order: 1 },
        { labelKey: 'foundations.nav.parameterHistory', to: '/foundations/parameters/history', order: 2 },
      ],
    },
    // The parameters' «Approvazioni in attesa» as a page (DEC-89): the rail's entry for an app with
    // no decision queue of its own. The same queue stands under the register's table.
    { labelKey: 'foundations.nav.parameterApprovals', to: '/foundations/parameters/approvals', order: 10.5 },
    { labelKey: 'foundations.nav.hierarchies', to: '/foundations/hierarchies', order: 11 },
    { labelKey: 'foundations.nav.plans', to: '/foundations/plans', order: 12 },
    { labelKey: 'foundations.nav.scenarios', to: '/foundations/scenarios', order: 13 },
    { labelKey: 'foundations.nav.exceptions', to: '/foundations/exceptions', order: 14 },
    // Beside the queue they fill (bata-ui#166 W3-5, ADR 2103): which rules check what.
    { labelKey: 'foundations.nav.exceptionRules', to: '/foundations/exception-rules', order: 14.5 },
    // Feeds and Schedules: one page each (client rule 3), sub-entries of Data in the rail (rule 1).
    {
      labelKey: 'foundations.nav.feeds',
      to: '/foundations/feeds',
      order: 15,
      children: [
        { labelKey: 'foundations.nav.feedsOnly', to: '/foundations/feeds', order: 1 },
        { labelKey: 'foundations.nav.schedules', to: '/foundations/schedules', order: 2 },
      ],
    },
    // Beside Data and schedules: what a planner reads there (a module skipped) is changed here.
    { labelKey: 'foundations.nav.modules', to: '/foundations/modules', order: 15.5 },
    { labelKey: 'foundations.nav.objectiveConfigs', to: '/foundations/objective-configs', order: 16 },
    // bata-reqs issue 38 R2 (ADR 9068): the market's process calendar — Pricing's rail lists it after
    // «Registro fasi di vita» (`../rail-apps.ts`).
    { labelKey: 'foundations.nav.processCalendar', to: '/foundations/process-calendar', order: 16.5 },
    // Beside the other governance screen, and still inside the chapter's band (20 is ch01's).
    { labelKey: 'foundations.nav.autonomy', to: '/foundations/autonomy', order: 17 },
    // bata-ui#218 (ADR 9037): who did what and when, in one place — Management's, before its common entries.
    { labelKey: 'foundations.nav.activity', to: '/foundations/activity', order: 17.5 },
    { labelKey: 'foundations.nav.documentation', to: '/foundations/documentation', order: 18 },
  ],
  routes: [
    {
      path: 'hierarchies',
      titleKey: 'foundations.hierarchies.title',
      urlParams: [],
      component: () => import('./screens/HierarchyAdminScreen'),
      surfaces: ['table', 'tile'],
      exports: { id: 'foundations.hierarchies.table', formats: ['csv', 'xlsx'], resolve: hierarchyExport },
      drill: { to: '/foundations/hierarchies/:schemeId', grain: 'category', alsoTo: ['/foundations/plans'] },
    },
    {
      path: 'plans',
      titleKey: 'foundations.plans.title',
      urlParams: [],
      component: () => import('./screens/PlanVersionScreen'),
      // `grid` stays declared (ADR 0085: `surfaces` is what the route is FOR) although the
      // fixture-backed diff grid was removed in F8 (2026-09-27); it returns when a read
      // publishes a version's cells. Until then the version history is what leaves.
      surfaces: ['grid', 'table'],
      exports: { id: 'foundations.plans.table', formats: ['csv', 'xlsx'], resolve: planVersionExport },
      drill: { to: '/foundations/plans/:versionId', grain: 'sku', alsoTo: ['/foundations/scenarios'] },
    },
    {
      path: 'versions',
      titleKey: 'foundations.versions.title',
      urlParams: [],
      component: () => import('./screens/PlanVersionScreen'),
      // Mounts the same `PlanVersionScreen` as `plans`, and declares `grid` for the same
      // reason: the diff grid is this screen's purpose, and it was removed in F8
      // (2026-09-27) only because nothing yet publishes its cells. See ADR 0085.
      surfaces: ['grid', 'table'],
      exports: { id: 'foundations.versions.table', formats: ['csv', 'xlsx'], resolve: planVersionExport },
      drill: { to: '/foundations/plans', grain: 'sku' },
    },
    {
      path: 'scenarios',
      titleKey: 'foundations.scenarios.title',
      urlParams: [],
      component: () => import('./screens/ScenarioCompareScreen'),
      // `tile` and `table` since F8 (2026-09-27): the fixture-backed bridge chart was
      // removed, and the export is the scenario list the switcher reads.
      surfaces: ['tile', 'table'],
      exports: { id: 'foundations.scenarios.table', formats: ['csv', 'xlsx'], resolve: scenarioListExport },
      drill: { to: '/foundations/plans', grain: 'category', alsoTo: ['/foundations/exceptions'] },
    },
    {
      path: 'exceptions',
      titleKey: 'foundations.exceptions.title',
      urlParams: ['article', 'by', 'country', 'department', 'key', 'overdue', 'owner', 'queue', 'rule', 'state', 'store', 'sub', 'view'],
      component: () => import('./screens/ExceptionQueueScreen'),
      surfaces: ['table'],
      exports: { id: 'foundations.exceptions.table', formats: ['csv', 'xlsx'], resolve: exceptionQueueExport },
      drill: { to: '/foundations/exceptions/:alertId', grain: 'store' },
    },
    {
      // DEC-101 (ADR 9040): one alert group — one alert type in one market, store or department —
      // opened by its filter (`?rule=&by=&key=`), its alerts ranked fifty at a time, acts on the set.
      path: 'exceptions/group',
      titleKey: 'foundations.alertGroups.page.title',
      urlParams: ['alert', 'article', 'by', 'country', 'department', 'key', 'overdue', 'queue', 'rule', 'state', 'store', 'sub', 'view'],
      component: () => import('./screens/AlertGroupScreen'),
      surfaces: ['table'],
      exports: { id: 'foundations.alertGroup.table', formats: ['csv', 'xlsx'], resolve: alertGroupExport },
      drill: { to: '/foundations/exceptions/:alertId', grain: 'store' },
    },
    {
      // bata-ui#166 W3-5 (ADR 2103, bata-platform ADR 9101): the rules and their governed change.
      path: 'exception-rules',
      titleKey: 'foundations.rules.title',
      urlParams: [],
      component: () => import('./screens/ExceptionRulesScreen'),
      surfaces: ['table'],
      exports: { id: 'foundations.rules.table', formats: ['csv', 'xlsx'], resolve: exceptionRulesExport },
      drill: { to: '/foundations/exceptions', grain: 'category' },
    },
    {
      path: 'feeds',
      titleKey: 'foundations.feeds.title',
      urlParams: ['country'],
      component: () => import('./screens/FeedStatusScreen'),
      surfaces: ['table', 'tile'],
      exports: { id: 'foundations.feeds.table', formats: ['csv', 'xlsx'], resolve: feedStatusExport },
      // A feed's batch history is the next level down, and it is not built until the
      // Dagster asset graph is publishing batch metadata (M3). Declared as a target
      // rather than as a terminal, because it is a route that will exist.
      drill: { to: '/foundations/feeds/:feedId', grain: 'day', alsoTo: ['/foundations/modules'] },
    },
    {
      path: 'schedules',
      titleKey: 'foundations.schedules.screenTitle',
      urlParams: ['country'],
      component: () => import('./screens/SchedulesScreen'),
      surfaces: ['table'],
      exports: { id: SCHEDULES_EXPORT_ID, formats: ['csv', 'xlsx'], resolve: schedulesExport },
      drill: {
        terminal: true,
        reason: 'A schedule is the grain the orchestrator reports; its runs are the jobs it launches, which bata-data owns and this platform does not publish per run.',
      },
    },
    {
      // DEC-06 self-management (bata-ui#166 W1-7, ADR 2006): read per market, changed only by a
      // registry proposal someone else approves.
      path: 'modules',
      titleKey: 'foundations.modules.screenTitle',
      urlParams: ['country'],
      component: () => import('./screens/ModulesScreen'),
      surfaces: ['table'],
      exports: { id: 'foundations.modules.table', formats: ['csv', 'xlsx'], resolve: modulesExport },
      drill: {
        terminal: true,
        reason: 'A module row IS the finest grain: GET /foundations/modules is keyed on (country, module) and publishes nothing beneath it. What a planner wants next from a row is who changed the setting and why, which is the registry key\'s history on the parameter screen, not a finer perimeter.',
      },
    },
    {
      path: 'parameters',
      titleKey: 'foundations.parameters.title',
      urlParams: ['ap.apps', 'ap.may', 'app', 'asOf', 'keys', 'parameter', 'q', 'scope', 'state'],
      component: () => import('./screens/ParameterRegistryScreen'),
      surfaces: ['table'],
      exports: {
        id: 'foundations.parameters.table',
        formats: ['csv', 'xlsx'],
        resolve: parameterRegistryExport,
      },
      // ENABLED rather than `disabled: true`. The screen reads two published endpoints
      // and writes through a third; nothing on it is fixture-backed, and every control it
      // cannot complete states its own reason on the row. A disabled nav entry is for a
      // screen a planner cannot yet use, and this one is usable the moment a date is on
      // the link.
      drill: {
        terminal: true,
        reason: 'A parameter row IS the finest grain: plan.parameter is keyed on (key, scope dimension, scope value, validity window) and there is nothing beneath it to drill to. What a planner wants next from a row is its HISTORY, which is the change record rather than a finer perimeter — and the recorded-baseline column already carries the one step of it that a reset depends on. A change-record browser over GET /registry/changes is a screen of its own and is not a drill target of this one.',
      },
    },
    {
      // DEC-89: the expiries and the change record, off the register's page (brief part 2 §6, model E).
      path: 'parameters/history',
      titleKey: 'foundations.parameterHistory.title',
      urlParams: ['app', 'asOf'],
      component: () => import('./screens/ParameterHistoryScreen'),
      surfaces: ['table'],
      exports: { id: 'foundations.parameterHistory.table', formats: ['csv', 'xlsx'], resolve: parameterHistoryExport },
      drill: {
        terminal: true,
        reason: 'A change set is the grain the register records a decision at; its items are shown inside it and its rows are the register itself, one level up. There is nothing beneath it to drill to.',
      },
    },
    {
      // DEC-89: the parameters' pending approvals as a page (brief part 2 §3, model A).
      path: 'parameters/approvals',
      titleKey: 'foundations.parameterApprovals.title',
      urlParams: ['ap.apps', 'ap.may', 'app'],
      component: () => import('./screens/ParameterApprovalsScreen'),
      surfaces: ['table'],
      exports: { id: 'foundations.parameterApprovals.table', formats: ['csv', 'xlsx'], resolve: parameterApprovalsExport },
      drill: {
        terminal: true,
        reason: 'A pending change set is approved or rejected WHOLE (17 §4, v7 §2.8): its items are shown on its card, and there is no finer grain a decision is taken at.',
      },
    },
    {
      // bata-reqs issue 38 R2 (bata-platform ADR 2136, ADR 9068): a market's year of dated periods —
      // promotional, pricing, transition, outlet, blackout — edited (future ones only) or imported
      // from the commercial plan, as one change set approved in Pricing's queue.
      path: 'process-calendar',
      titleKey: 'foundations.calendar.title',
      urlParams: ['country', 'year'],
      component: () => import('./screens/ProcessCalendarScreen'),
      surfaces: ['table'],
      exports: { id: 'foundations.processCalendar.table', formats: ['csv', 'xlsx'], resolve: processCalendarExport },
      drill: {
        terminal: true,
        reason: 'A period IS the finest grain the calendar keeps: GET /registry/process-calendar is keyed on (market, period id) and publishes nothing beneath it. What a planner wants next from a period is its versions, which are the history panel on the same page, and the runs it launches, which are the end-of-life and price pages the rail already reaches.',
      },
    },
    {
      path: 'objective-configs',
      titleKey: 'foundations.objectiveConfigs.title',
      urlParams: [],
      component: () => import('./screens/ObjectiveConfigProposalScreen'),
      // A form, not an aggregate: `NonDataRoute` — `exports`/`drill` are `never` on it,
      // the same treatment `documentation` gets below, because there is no table here to
      // export and no finer grain to drill to. The change this form writes is inspected
      // and approved on `/foundations/parameters`'s pending-approvals panel, not here.
      surfaces: ['form'],
    },
    {
      path: 'autonomy',
      titleKey: 'foundations.autonomy.title',
      urlParams: [],
      component: () => import('./screens/AutonomyRegisterScreen'),
      surfaces: ['table'],
      exports: { id: 'foundations.autonomy.table', formats: ['csv', 'xlsx'], resolve: autonomyRegisterExport },
      drill: {
        terminal: true,
        reason: 'A register row IS the finest grain: GET /conformance/autonomy-register is keyed on (decision class, country) and publishes nothing beneath it. What a planner wants next from a row is the decisions made under that grant, and no read publishes those per decision class yet — the override-rate read is per exit reason code, not per autonomy row. A drill target is declared when a read exists to land on.',
      },
    },
    {
      // bata-ui#218 phases 0–2 (ADR 9037): the activity register — who did what and when. Gated on
      // the platform's `activity · read` (ADR 9022): the router states the reason in its place, and
      // the rail and the palette do not offer it, when the platform's answer denies it.
      path: 'activity',
      titleKey: 'foundations.activity.title',
      urlParams: ['action', 'actor', 'app', 'country', 'from', 'object', 'to'],
      component: () => import('./screens/ActivityScreen'),
      surfaces: ['table'],
      capability: { kind: 'activity', action: 'read' },
      exports: { id: ACTIVITY_EXPORT_ID, formats: ['csv', 'xlsx'], resolve: activityExport },
      drill: {
        terminal: true,
        reason: 'An activity row is one recorded action, the finest grain the register keeps. Its object opens the page of that object when the platform names one (`link`), which is a cross-link to where the object lives, not a finer perimeter of this list.',
      },
    },
    {
      // ADR 9059 (bata-platform ADR 9029): «Attività utenti» — who signed in, for how long, and what
      // they changed. Administrator only, twice over: the realm role (`requires`, as User management's
      // shell route) and the platform's `user_activity · read`. A chapter route rather than a shell
      // one because exports and drills are declared on a manifest; it is reached from Admin (the
      // account menu, the palette and User management's header), not from the rail.
      path: 'user-activity',
      titleKey: 'foundations.userActivity.title',
      urlParams: ['from', 'sort', 'to', 'user'],
      component: () => import('./screens/UserActivityScreen'),
      surfaces: ['table'],
      requires: ['administrator'],
      capability: { kind: 'user_activity', action: 'read' },
      exports: { id: USER_ACTIVITY_EXPORT_ID, formats: ['csv', 'xlsx'], resolve: userActivityExport },
      drill: { to: '/foundations/user-activity/:userId', grain: 'transaction', alsoTo: ['/foundations/user-activity/:userId/actions'] },
    },
    {
      path: 'user-activity/:userId',
      titleKey: 'foundations.userActivity.sessions.title',
      urlParams: ['from', 'to'],
      component: () => import('./screens/UserActivityDetailScreen'),
      surfaces: ['table'],
      requires: ['administrator'],
      capability: { kind: 'user_activity', action: 'read' },
      exports: { id: USER_SESSIONS_EXPORT_ID, formats: ['csv', 'xlsx'], resolve: userSessionsExport },
      drill: {
        terminal: true,
        reason: 'A session is the finest grain the identity broker\'s events are rebuilt into: its sign-in, refreshes and end are summarised on its row and nothing beneath it is published. The person\'s actions are a sibling view (`/foundations/user-activity/:userId/actions`), not a finer perimeter of a session.',
      },
    },
    {
      path: 'user-activity/:userId/actions',
      titleKey: 'foundations.userActivity.actions.title',
      urlParams: ['from', 'to'],
      component: () => import('./screens/UserActivityDetailScreen'),
      surfaces: ['table'],
      requires: ['administrator'],
      capability: { kind: 'user_activity', action: 'read' },
      exports: { id: USER_ACTIONS_EXPORT_ID, formats: ['csv', 'xlsx'], resolve: userActionsExport },
      drill: {
        terminal: true,
        reason: 'A row is one kind of act (method, route template, object kind) with its count, the grain GET /admin/user-activity/{user_id}/actions publishes, and nothing beneath it — the single acts behind a count — is published per person by that read.',
      },
    },
    {
      path: 'documentation',
      titleKey: 'foundations.documentation.title',
      urlParams: ['app', 'country', 'guide', 'keys', 'section', 'stage'],
      component: () => import('./screens/DocumentationScreen'),
      surfaces: ['detail'],
    },
  ],
});
